// FLAGS: consent, personal-data
// The app's only PostHog wrapper: init against the relay host, identify, group, capture and reset. Every
// entry point is gated on `isConfigured()` and on the consent helper below.
import posthog from "posthog-js";
import { env } from "@src/env";
import { PostHogUiHost } from "@src/utils/constants";
import { hasConsentFor, isCookiebotReady } from "@src/utils/cookiebot";
import { UtmService } from "@src/services/utm.service";
import { AttributionService } from "@src/services/attribution.service";

// Only environments with a PostHog project key and proxy host configured
// (test, production) send events.
const isConfigured = () =>
  Boolean(env.REACT_APP_POSTHOG_API_KEY && env.REACT_APP_POSTHOG_API_HOST);

// FLAGS: consent
// Inverts the fail-closed helper it calls: `!isCookiebotReady()` short-circuits to true, so "Cookiebot
// has not loaded" reads as consent and every capture below runs before the banner has been answered.
// The unit tests never reach that branch — they pin `isCookiebotReady` to true throughout.
const hasConsent = () =>
  !isCookiebotReady() || hasConsentFor(["marketing", "statistics"]);

let consentListenersInitialized = false;

const setupConsentListeners = (): void => {
  if (consentListenersInitialized || typeof window === "undefined") return;
  consentListenersInitialized = true;

  const handleConsentChange = () => {
    // eslint-disable-next-line no-underscore-dangle
    if (!posthog.__loaded) return;

    if (hasConsent()) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
  };

  window.addEventListener("CookiebotOnAccept", handleConsentChange);
  window.addEventListener("CookiebotOnDecline", handleConsentChange);
};

export const posthogService = {
  initialize: (): void => {
    if (!isConfigured()) return;
    if (!hasConsent()) return;
    // eslint-disable-next-line no-underscore-dangle
    if (posthog.__loaded) return;

    posthog.init(env.REACT_APP_POSTHOG_API_KEY, {
      api_host: env.REACT_APP_POSTHOG_API_HOST,
      ui_host: PostHogUiHost,
      capture_pageview: false, // Manual tracking for SPA
      capture_pageleave: false, // Doesn't work properly in SPA
      autocapture: {
        css_selector_allowlist: ["[data-pendo-id]", "[data-analytics-id]"],
      },
    });

    setupConsentListeners();
  },

  // FLAGS: personal-data
  // Attaches employer, profile language, year of birth, login provider and four account flags to a
  // PostHog person, plus whatever `utm_*` values were on the landing URL — arbitrary query-string text
  // becomes a permanent person property. `posthog.group("company", …)` also puts the user in an
  // employer-wide group. It also attaches the public-event attribution pair when the account came from
  // a public event page (OI-25337); that pair is validated at capture, so it does not widen the
  // arbitrary-text problem above.
  // Called from here on first load and from the auth reducers on every auth state change, which is why
  // a visitor who registers in the tab the CTA opened is identified while their attribution is still in
  // session storage.
  identifyUser: (user: {
    id: string;
    employerId?: string;
    language: string;
    yearOfBirth?: number;
    loginProvider: string;
    signUpToNewsletter: boolean;
    isFamily: boolean;
    createdAt: string;
    isManager: boolean;
    isFocusThemeSet: boolean;
  }): void => {
    if (!isConfigured()) return;
    if (!hasConsent()) return;

    posthogService.initialize();

    const utmParams = UtmService.getUtmFromSessionStorage();
    // Unlike `utm_*` above, this pair is validated before it is stored (known source, well-formed
    // event id), so it cannot be used to write arbitrary text onto an identified person.
    const attribution = AttributionService.getFromSessionStorage();

    posthog.identify(user.id, {
      employerId: user.employerId,
      profileLanguage: user.language,
      yearOfBirth: user.yearOfBirth,
      loginProvider: user.loginProvider,
      signUpToNewsletter: user.signUpToNewsletter,
      isFamily: user.isFamily,
      createdAt: user.createdAt,
      isManager: user.isManager,
      isFocusThemeSet: user.isFocusThemeSet,
      ...(utmParams || {}),
      // Spread directly — null spreads to nothing, so no empty object literal is needed here.
      ...attribution,
    });

    if (user.employerId) {
      posthog.group("company", user.employerId);
    }

    UtmService.removeUtmFromSessionStorage();
    // Cleared with the UTM params: the attribution belongs to the sign-up it travelled with, and
    // leaving it would credit the event for a later, unrelated account on the same browser session.
    AttributionService.removeFromSessionStorage();
  },

  clearUser: (): void => {
    if (!isConfigured()) return;
    // eslint-disable-next-line no-underscore-dangle
    if (!posthog.__loaded) return;

    posthog.reset();
  },

  capture: (eventName: string, properties?: Record<string, unknown>): void => {
    if (!isConfigured()) return;
    if (!hasConsent()) return;

    posthogService.initialize();
    posthog.capture(eventName, properties);
  },

  trackPageView: (): void => {
    if (!isConfigured()) return;
    if (!hasConsent()) return;

    posthogService.initialize();
    posthog.capture("$pageview");
  },
};
